/**
 * Construit un graphique Plotly (barres) à partir du bloc « charts » renvoyé par
 * `GET …/data/source/:id/pv` (`energie_elec_tout` ou `extrapolation_pv_daily`),
 * avec layout et survol alignés sur les helpers du module data
 * (`@modules/data/utils/plotly`).
 */

import type { PlotlyGraph } from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import {
    applyHomeysStandardTraceHover,
    buildHomeysBarHoverTemplateYAndTraceName,
    buildHomeysStandardPlotlyLayout,
    extractTooltipUnitFromAxisLabel,
} from "@modules/data/utils/plotly"

export type PvConsommationPlotlyGraph = PlotlyGraph

export type BuildPvConsommationPlotlyGraphOptions = {
    /** Titre court / repli si pas de libellés de légende dédiés. */
    chartTitle?: string
    /** Libellé de la série principale (légende Plotly + tooltip). */
    legendPrimary: string
    /** Libellé de la série « secondaire » (extrapolé / hors horaire), si des points existent. */
    legendSecondary: string
    /**
     * Libellé axe Y (ex. kWh) : si défini, remplace celui lu sur le bloc courant
     * (le bloc extrapolation peut avoir un `y_label` différent du bloc consommation Enedis).
     */
    yAxisLabel?: string
}

type BarPoint = {
    x?: unknown
    y?: unknown
    c_color?: string
    extrapolation?: boolean
}

function asRecord(v: unknown): Record<string, unknown> | null {
    if (typeof v !== "object" || v === null) return null
    return v as Record<string, unknown>
}

function normalizeX(raw: unknown): string {
    if (raw instanceof Date) return raw.toISOString()
    if (typeof raw === "string") return raw
    return String(raw ?? "")
}

function isSecondaryBarPoint(
    row: Record<string, unknown>,
    baseColor: string
): boolean {
    if (row.extrapolation === true) return true
    const cc = row.c_color
    return typeof cc === "string" && cc.length > 0 && cc !== baseColor
}

/** Couleurs « vides » overlay : Plotly peut prendre l’index 0 pour le carré de légende → swatch invisible. */
function isFullyTransparentMarkerColor(color: string): boolean {
    const t = color.trim().toLowerCase()
    if (t === "transparent") return true
    const m = t.match(
        /^rgba\s*\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)$/
    )
    if (m) return Number.parseFloat(m[1]) === 0
    return false
}

/** Première couleur réelle d’une série barres (y non nul), pour l’icône de légende. */
function firstVisibleBarMarkerColor(
    yValues: (number | null)[],
    colors: string[],
    fallback: string
): string {
    for (let i = 0; i < yValues.length; i++) {
        if (yValues[i] == null) continue
        const c = colors[i]
        if (
            typeof c === "string" &&
            c.length > 0 &&
            !isFullyTransparentMarkerColor(c)
        ) {
            return c
        }
    }
    for (const c of colors) {
        if (
            typeof c === "string" &&
            c.length > 0 &&
            !isFullyTransparentMarkerColor(c)
        ) {
            return c
        }
    }
    return fallback
}

/**
 * Plotly s’appuie souvent sur `marker.color[0]` pour le carré de légende.
 * Avec overlay, l’index 0 peut être `rgba(0,0,0,0)` alors qu’il n’y a pas de barre
 * (`y` null) : remplacer par la couleur représentative ne change pas le rendu.
 */
function patchTransparentLeadingLegendSwatchColor(
    colors: string[],
    representative: string
): void {
    if (colors.length === 0) return
    if (!isFullyTransparentMarkerColor(colors[0])) return
    colors[0] = representative
}

/**
 * Libellé d’axe Y déduit du bloc graphique (barplot) — même règle que le build du graphique.
 */
export function extractPvConsommationYAxisLabel(chartBlock: unknown): string {
    const root = asRecord(chartBlock)
    if (!root) return ""
    const subs = root.subcharts
    if (typeof subs !== "object" || subs === null) return ""

    let barplot: Record<string, unknown> | null = null
    for (const sub of Object.values(subs as Record<string, unknown>)) {
        const s = asRecord(sub)
        if (s && String(s.type) === "barplot") {
            barplot = s
            break
        }
    }
    if (!barplot) return ""

    const content = asRecord(barplot.content)
    const yLabel =
        (typeof content?.y_label === "string" && content.y_label) ||
        (typeof root.y_label === "string" && root.y_label) ||
        (typeof root.unit === "string" && root.unit) ||
        ""
    return typeof yLabel === "string" ? yLabel.trim() : ""
}

/**
 * @param chartBlock - Objet avec `subcharts` (ex. `variables.energie_elec_tout`)
 */
export function buildPvConsommationPlotlyGraph(
    chartBlock: unknown,
    options: BuildPvConsommationPlotlyGraphOptions
): PvConsommationPlotlyGraph | null {
    const root = asRecord(chartBlock)
    if (!root) return null
    const subs = root.subcharts
    if (typeof subs !== "object" || subs === null) return null

    let barplot: Record<string, unknown> | null = null
    for (const sub of Object.values(subs as Record<string, unknown>)) {
        const s = asRecord(sub)
        if (s && String(s.type) === "barplot") {
            barplot = s
            break
        }
    }
    if (!barplot) return null

    const content = asRecord(barplot.content)
    const rawPoints = content?.points
    if (!Array.isArray(rawPoints) || rawPoints.length === 0) return null

    const colors = (content?.colors as string[] | undefined) ?? []
    const baseColor =
        typeof colors[0] === "string" && colors[0].length > 0
            ? colors[0]
            : "#3b82f6"

    const points = rawPoints.map((p) => {
        const row = asRecord(p) ?? {}
        const y =
            typeof row.y === "number"
                ? row.y
                : Number(typeof row.y === "string" ? row.y : NaN)
        const cc =
            typeof row.c_color === "string" && row.c_color.length > 0
                ? row.c_color
                : baseColor
        return {
            x: normalizeX(row.x),
            y: Number.isFinite(y) ? y : 0,
            color: cc,
            row,
        }
    })

    const xLabel = typeof root.x_label === "string" ? root.x_label : "Date"
    const yLabelFromBlock = extractPvConsommationYAxisLabel(chartBlock)
    const yLabel =
        typeof options.yAxisLabel === "string" &&
        options.yAxisLabel.trim() !== ""
            ? options.yAxisLabel.trim()
            : yLabelFromBlock

    const yTooltipUnit = extractTooltipUnitFromAxisLabel(yLabel.trim())
    const hovertemplate = buildHomeysBarHoverTemplateYAndTraceName({
        unit: yTooltipUnit,
    })

    const hasSecondary = points.some((p) =>
        isSecondaryBarPoint(p.row, baseColor)
    )

    const traceNameFallback =
        options.chartTitle ??
        (typeof root.name === "string" ? root.name : "Consommation")

    const traces: object[] = []

    if (hasSecondary) {
        const xs = points.map((p) => p.x)
        const yPrimary: (number | null)[] = []
        const ySecondary: (number | null)[] = []
        const colPrimary: string[] = []
        const colSecondary: string[] = []

        for (const p of points) {
            const sec = isSecondaryBarPoint(p.row, baseColor)
            if (sec) {
                yPrimary.push(null)
                ySecondary.push(p.y)
                colPrimary.push("rgba(0,0,0,0)")
                colSecondary.push(p.color)
            } else {
                yPrimary.push(p.y)
                ySecondary.push(null)
                colPrimary.push(p.color)
                colSecondary.push("rgba(0,0,0,0)")
            }
        }

        const primaryLegendColor = firstVisibleBarMarkerColor(
            yPrimary,
            colPrimary,
            baseColor
        )
        const secondaryPaletteFallback =
            typeof colors[1] === "string" && colors[1].trim().length > 0
                ? colors[1].trim()
                : baseColor
        const secondaryLegendColor = firstVisibleBarMarkerColor(
            ySecondary,
            colSecondary,
            secondaryPaletteFallback
        )
        patchTransparentLeadingLegendSwatchColor(colPrimary, primaryLegendColor)
        patchTransparentLeadingLegendSwatchColor(
            colSecondary,
            secondaryLegendColor
        )

        traces.push({
            type: "bar",
            x: xs,
            y: yPrimary,
            name: options.legendPrimary,
            meta: [options.legendPrimary],
            marker: { color: colPrimary },
            hovertemplate,
        })
        traces.push({
            type: "bar",
            x: xs,
            y: ySecondary,
            name: options.legendSecondary,
            meta: [options.legendSecondary],
            marker: { color: colSecondary },
            hovertemplate,
        })
    } else {
        traces.push({
            type: "bar",
            x: points.map((p) => p.x),
            y: points.map((p) => p.y),
            name: options.legendPrimary || traceNameFallback,
            meta: [options.legendPrimary || traceNameFallback],
            marker: { color: points.map((p) => p.color) },
            hovertemplate,
        })
    }

    const data = applyHomeysStandardTraceHover(traces, {
        yAxisLabel: yLabel,
    })

    const layoutMerged = buildHomeysStandardPlotlyLayout(
        {
            autosize: true,
            bargap: 0.15,
            ...(hasSecondary ? { barmode: "overlay" } : {}),
            showlegend: true,
            xaxis: {},
            yaxis: { rangemode: "tozero" },
        },
        {
            x_label: xLabel,
            y_label: yLabel,
            name: "pv_consommation_daily",
        },
        { dateXAxisFr: true }
    )

    return {
        data,
        layout: layoutMerged,
    }
}

/** Premier sous-graphique `barplot` (pour légende / métadonnées). */
export function getFirstBarplotFromChartBlock(
    chartBlock: unknown
): { content?: { colors?: string[]; points?: BarPoint[] } } | null {
    const root = asRecord(chartBlock)
    if (!root?.subcharts || typeof root.subcharts !== "object") return null
    for (const sub of Object.values(
        root.subcharts as Record<string, unknown>
    )) {
        const s = asRecord(sub)
        if (s && String(s.type) === "barplot") {
            return {
                content: s.content as {
                    colors?: string[]
                    points?: BarPoint[]
                },
            }
        }
    }
    return null
}

/** Indique si le barplot porte au moins un point « secondaire » (couleur ou flag extrapolation). */
export function getBarplotHasSecondaryTone(chartBlock: unknown): boolean {
    const root = asRecord(chartBlock)
    if (!root?.subcharts || typeof root.subcharts !== "object") return false
    for (const sub of Object.values(
        root.subcharts as Record<string, unknown>
    )) {
        const s = asRecord(sub)
        if (!s || String(s.type) !== "barplot") continue
        const content = asRecord(s.content)
        const colors = (content?.colors as string[] | undefined) ?? []
        const baseColor =
            typeof colors[0] === "string" && colors[0].length > 0
                ? colors[0]
                : "#3b82f6"
        const rawPoints = content?.points
        if (!Array.isArray(rawPoints)) continue
        for (const p of rawPoints) {
            const row = asRecord(p) ?? {}
            if (isSecondaryBarPoint(row, baseColor)) return true
        }
    }
    return false
}
